import {
  DeploymentLogType,
  DeploymentScheduleType,
  DeploymentSchedulerConfigType,
  DeploymentStatusType,
} from "@/interfaces";
import { useQuery } from "@tanstack/react-query";
import api from "./api";

/**
 * Query keys for deployment queries
 */
export const deploymentKeys = {
  status: ["deploymentStatus"] as const,
  all: ["deployments"] as const,
  list: (params: { page: number; limit: number }) => [...deploymentKeys.all, "list", params] as const,
  log: (id: string) => [...deploymentKeys.all, "log", id] as const,
};

// Poll faster while a deployment is active so the phase changes (login block, start, finish) show up promptly
const IDLE_POLL_MS = 30_000;
const ACTIVE_POLL_MS = 10_000;
// Deployments page: refresh history and a running log this often while a deployment is active
const ADMIN_ACTIVE_POLL_MS = 5_000;

/**
 * React Query hook polling the scheduled/running deployment. Public endpoint: works on the login page too.
 * While the server restarts mid-deployment the request fails and the last status (inProgress) is kept.
 */
export const useDeploymentStatus = () => {
  return useQuery<DeploymentStatusType>({
    queryKey: deploymentKeys.status,
    queryFn: async () => {
      const response = await api.get<DeploymentStatusType>("/deployment/status");
      return response.data;
    },
    refetchInterval: (query) => (query.state.data?.deployment ? ACTIVE_POLL_MS : IDLE_POLL_MS),
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    retry: false,
  });
};

export interface DeploymentListResponse {
  data: DeploymentScheduleType[];
  meta: { total: number; page: number; limit: number; totalPages: number };
  scheduler: DeploymentSchedulerConfigType;
}

export interface ScheduleDeploymentPayload {
  deployAt: string;
  loginBlockMinutesBefore: number;
  notifyMinutesBefore: number;
  message?: string | null;
}

const isActive = (status?: string) => status === "scheduled" || status === "running";

/**
 * React Query hook for the Super Admin deployment history (polls while a deployment is active)
 */
export const useDeployments = (params: { page: number; limit: number }) => {
  return useQuery<DeploymentListResponse>({
    queryKey: deploymentKeys.list(params),
    queryFn: async () => {
      const response = await api.get<DeploymentListResponse>("/deployment", { params });
      return response.data;
    },
    refetchInterval: (query) =>
      query.state.data?.data.some((deployment) => isActive(deployment.status)) ? ADMIN_ACTIVE_POLL_MS : false,
  });
};

/**
 * React Query hook for a deployment's script output; follows the log while the deployment runs
 */
export const useDeploymentLog = (id: string | null) => {
  return useQuery<DeploymentLogType>({
    queryKey: deploymentKeys.log(id ?? ""),
    queryFn: async () => {
      const response = await api.get<DeploymentLogType>(`/deployment/${id}/log`);
      return response.data;
    },
    enabled: !!id,
    refetchInterval: (query) => (isActive(query.state.data?.status) ? ADMIN_ACTIVE_POLL_MS : false),
  });
};

const deploymentService = {
  schedule: async (data: ScheduleDeploymentPayload): Promise<{ message: string; id: string }> => {
    const response = await api.post("/deployment", data);
    return response.data;
  },

  cancel: async (id: string): Promise<{ message: string }> => {
    const response = await api.post(`/deployment/${id}/cancel`);
    return response.data;
  },

  markFailed: async (id: string): Promise<{ message: string }> => {
    const response = await api.post(`/deployment/${id}/mark-failed`);
    return response.data;
  },
};

export default deploymentService;
